<!-- 
  Provides a current weather display with weather symbol, temperature and location.

  PROPS:
    appId (String): Specifies your project's APPID, received after registering with OpenWeatherMap.
    isWhite (Boolean): Displays content in white instead of black (true by default)
-->

<template>
  <div
    v-if="weather"
    :class="`ads-weather ${isWhite ? 'isWhite' : ''}`"
  >
    <div class="ads-weather__icon">
      <i
        :class="`wu wu-${isWhite ? 'white' : 'black'} wu-64 ${icon}`"
        :title="'Currently ' + weather"
      />
    </div>
    <div>
      <div class="ads-weather__temp">
        {{ currentTemp }}&#176;
      </div>
      <div class="ads-weather__city">
        {{ city }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import 'weather-underground-icons'

const weatherIcons = {
  '200': {
    label: 'thunderstorm with light rain',
    icon: 'chancetstorms'
  },

  '201': {
    label: 'thunderstorm with rain',
    icon: 'tstorms'
  },

  '202': {
    label: 'thunderstorm with heavy rain',
    icon: 'tstorms'
  },

  '210': {
    label: 'light thunderstorm',
    icon: 'chancetstorms'
  },

  '211': {
    label: 'thunderstorm',
    icon: 'tstorms'
  },

  '212': {
    label: 'heavy thunderstorm',
    icon: 'tstorms'
  },

  '221': {
    label: 'ragged thunderstorm',
    icon: 'tstorms'
  },

  '230': {
    label: 'thunderstorm with light drizzle',
    icon: 'chancetstorms'
  },

  '231': {
    label: 'thunderstorm with drizzle',
    icon: 'chancetstorms'
  },

  '232': {
    label: 'thunderstorm with heavy drizzle',
    icon: 'tstorms'
  },

  '300': {
    label: 'light intensity drizzle',
    icon: 'chancerain'
  },

  '301': {
    label: 'drizzle',
    icon: 'chancerain'
  },

  '302': {
    label: 'heavy intensity drizzle',
    icon: 'rain'
  },

  '310': {
    label: 'light intensity drizzle rain',
    icon: 'chancerain'
  },

  '311': {
    label: 'drizzle rain',
    icon: 'rain'
  },

  '312': {
    label: 'heavy intensity drizzle rain',
    icon: 'rain'
  },

  '313': {
    label: 'shower rain and drizzle',
    icon: 'rain'
  },

  '314': {
    label: 'heavy shower rain and drizzle',
    icon: 'rain'
  },

  '321': {
    label: 'shower drizzle',
    icon: 'rain'
  },

  '500': {
    label: 'light rain',
    icon: 'rain'
  },

  '501': {
    label: 'moderate rain',
    icon: 'rain'
  },

  '502': {
    label: 'heavy intensity rain',
    icon: 'rain'
  },

  '503': {
    label: 'very heavy rain',
    icon: 'rain'
  },

  '504': {
    label: 'extreme rain',
    icon: 'rain'
  },

  '511': {
    label: 'freezing rain',
    icon: 'chancesleet'
  },

  '520': {
    label: 'light intensity shower rain',
    icon: 'rain'
  },

  '521': {
    label: 'shower rain',
    icon: 'rain'
  },

  '522': {
    label: 'heavy intensity shower rain',
    icon: 'rain'
  },

  '531': {
    label: 'ragged shower rain',
    icon: 'rain'
  },

  '600': {
    label: 'light snow',
    icon: 'chancesnow'
  },

  '601': {
    label: 'snow',
    icon: 'snow'
  },

  '602': {
    label: 'heavy snow',
    icon: 'snow'
  },

  '611': {
    label: 'sleet',
    icon: 'sleet'
  },

  '612': {
    label: 'shower sleet',
    icon: 'sleet'
  },

  '615': {
    label: 'light rain and snow',
    icon: 'sleet'
  },

  '616': {
    label: 'rain and snow',
    icon: 'chancesnow'
  },

  '620': {
    label: 'light shower snow',
    icon: 'chancesnow'
  },

  '621': {
    label: 'shower snow',
    icon: 'snow'
  },

  '622': {
    label: 'heavy shower snow',
    icon: 'snow'
  },

  '701': {
    label: 'mist',
    icon: 'fog'
  },

  '711': {
    label: 'smoke',
    icon: 'fog'
  },

  '721': {
    label: 'haze',
    icon: 'hazy'
  },

  '731': {
    label: 'sand, dust whirls',
    icon: 'hazy'
  },

  '741': {
    label: 'fog',
    icon: 'fog'
  },

  '751': {
    label: 'sand',
    icon: 'hazy'
  },

  '761': {
    label: 'dust',
    icon: 'dust'
  },

  '762': {
    label: 'volcanic ash',
    icon: 'smog'
  },

  '771': {
    label: 'squalls',
    icon: 'day-windy'
  },

  '781': {
    label: 'tornado',
    icon: 'tornado'
  },

  '800': {
    label: 'clear sky',
    icon: 'clear'
  },

  '801': {
    label: 'few clouds',
    icon: 'cloudy'
  },

  '802': {
    label: 'scattered clouds',
    icon: 'cloudy'
  },

  '803': {
    label: 'broken clouds',
    icon: 'cloudy'
  },

  '804': {
    label: 'overcast clouds',
    icon: 'cloudy'
  },

  '900': {
    label: 'tornado',
    icon: 'tornado'
  },

  '901': {
    label: 'tropical storm',
    icon: 'hurricane'
  },

  '902': {
    label: 'hurricane',
    icon: 'hurricane'
  },

  '903': {
    label: 'cold',
    icon: 'snowflake-cold'
  },

  '904': {
    label: 'hot',
    icon: 'hot'
  },

  '905': {
    label: 'windy',
    icon: 'windy'
  },

  '906': {
    label: 'hail',
    icon: 'hail'
  },

  '951': {
    label: 'calm',
    icon: 'sunny'
  },

  '952': {
    label: 'light breeze',
    icon: 'cloudy-gusts'
  },

  '953': {
    label: 'gentle breeze',
    icon: 'cloudy-gusts'
  },

  '954': {
    label: 'moderate breeze',
    icon: 'cloudy-gusts'
  },

  '955': {
    label: 'fresh breeze',
    icon: 'cloudy-gusts'
  },

  '956': {
    label: 'strong breeze',
    icon: 'cloudy-gusts'
  },

  '957': {
    label: 'high wind, near gale',
    icon: 'cloudy-gusts'
  },

  '958': {
    label: 'gale',
    icon: 'cloudy-gusts'
  },

  '959': {
    label: 'severe gale',
    icon: 'cloudy-gusts'
  },

  '960': {
    label: 'storm',
    icon: 'thunderstorm'
  },

  '961': {
    label: 'violent storm',
    icon: 'thunderstorm'
  },

  '962': {
    label: 'hurricane',
    icon: 'cloudy-gusts'
  }
}

export default {
  name: 'Weather',
  props: {
    appId: {
      type: String,
      required: true
    },
    isWhite: { type: Boolean, default: true }
  },
  data() {
    return {
      currentTemp: '',
      icon: '',
      city: '',
      weather: ''
    }
  },
  created() {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          const Weather_URL = `https://api.openweathermap.org/data/2.5/weather?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}&APPID=${this.appId}&units=metric`
          axios
            .get(Weather_URL)
            .then(response => {
              let weather = weatherIcons[response.data.weather[0].id]
              this.currentTemp = Math.round(response.data.main.temp)
              this.city = response.data.name
              this.weather = weather.label
              this.icon = 'wu-' + weather.icon
            })
            .catch(error => {
              console.log(error) // eslint-disable-line no-console
            })
        },
        err => {
          console.log(err) // eslint-disable-line no-console
          const Weather_URL = `https://api.openweathermap.org/data/2.5/weather?q=Sydney,au&APPID=${this.appId}&units=metric`
          axios
            .get(Weather_URL)
            .then(response => {
              let weather = weatherIcons[response.data.weather[0].id]
              this.currentTemp = Math.round(response.data.main.temp)
              this.city = response.data.name
              this.weather = weather.label
              this.icon = 'wu-' + weather.icon
            })
            .catch(error => {
              console.log(error) // eslint-disable-line no-console
            })
        }
      )
    }
  }
}
</script>

<style scoped lang="scss">
.ads-weather {
  display: inline-flex;
  align-items: center;
  text-align: left;
  color: black;
  &.isWhite {
    color: white;
  }
}

.ads-weather__icon {
  padding-right: 12px;
}

.ads-weather__temp {
  font-size: 40px;
  font-weight: bold;
}

.ads-weather__city {
  font-size: 20px;
}
</style>
